import React from "react";
import { Check, Minus, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const packages = [{
  id: "starter",
  name: "Starter",
  price: "$995",
  gst: "+ GST",
  link: "/StarterPackagePage",
  cta: "View Starter",
  description: "Perfect for small businesses needing a professional start."
}, {
  id: "advanced",
  name: "Advanced",
  price: "$1400",
  gst: "+ GST",
  link: "/AdvancedPackagePage",
  cta: "View Advanced",
  description: "More pages and features for growing businesses."
}, {
  id: "premium",
  name: "Premium",
  price: "$1995",
  gst: "+ GST",
  link: "/ContactPage",
  cta: "Enquire Now",
  description: "The complete digital solution for high-growth businesses."
}];
const comparisonData = [{
  feature: "Monthly Updates & Maint.",
  starter: "$39/mo",
  advanced: "$49/mo",
  premium: "$69/mo"
}, {
  feature: "Number of Pages",
  starter: "Up to 3",
  advanced: "Up to 5",
  premium: "Up to 10"
}, {
  feature: "Custom Modern Design",
  starter: true,
  advanced: true,
  premium: true
}, {
  feature: "Mobile Responsive",
  starter: true,
  advanced: true,
  premium: true
}, {
  feature: "Hosting & Domain Included",
  starter: "12 Months",
  advanced: "12 Months",
  premium: "12 Months"
}, {
  feature: "Basic SEO Optimisation",
  starter: true,
  advanced: true,
  premium: true
}, {
  feature: "Contact Form & Click-to-call",
  starter: true,
  advanced: true,
  premium: true
}, {
  feature: "Monthly Content Updates",
  starter: "2",
  advanced: "5",
  premium: "10"
}, {
  feature: "Unlimited Tech Support",
  starter: true,
  advanced: true,
  premium: true
}, {
  feature: "Design Revisions",
  starter: "2 Rounds",
  advanced: "3 rounds",
  premium: "5 rounds"
}, {
  feature: "Branding Session",
  starter: false,
  advanced: "1 Hour",
  premium: "2 Hours"
}, {
  feature: "Google Map Integration",
  starter: true,
  advanced: true,
  premium: true
}, {
  feature: "Speed Optimisation",
  starter: true,
  advanced: true,
  premium: true
}];
const renderCell = (value: string | boolean) => {
  if (value === true) {
    return <Check className="size-5 text-primary mx-auto" />;
  }
  if (value === false) {
    return <Minus className="size-5 text-muted-foreground/30 mx-auto" />;
  }
  if (value === "N/A") {
    return <span className="text-muted-foreground/30">-</span>;
  }
  return <span className="font-medium text-foreground">{value}</span>;
};
export default function PackageComparisonGrid() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="package-comparison-grid" className="bg-background text-foreground py-20 px-4 md:px-8">
      <div ref={ref} className={cn("max-w-7xl mx-auto transition-all duration-700 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight">
            Compare Packages
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Detailed feature breakdown to help you choose the right solution for
            your business.
          </p>
        </div>

        <div className="hidden lg:block rounded-2xl overflow-hidden border border-border shadow-2xl">
          <Table className="w-full border-collapse">
            <TableHeader className="bg-muted/30">
              <TableRow className="border-b border-border hover:bg-transparent">
                <TableHead className="w-[25%] py-8 px-6 text-lg font-bold text-foreground">
                  Features
                </TableHead>
                {packages.map((pkg, i) => <TableHead key={pkg.id} data-index={i} className="w-[25%] py-8 px-6 text-center align-top">
                    <div className="flex flex-col items-center gap-4">
                      <div className="space-y-1">
                        {i === 1 && <div className="bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full w-fit mx-auto mb-2">
                            Popular
                          </div>}
                        <h3 className="text-xl font-bold text-primary">
                          {pkg.name}
                        </h3>
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-2xl font-bold text-foreground">
                            {pkg.price}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {pkg.gst}
                          </span>
                        </div>
                      </div>
                      <Button asChild variant={i === 1 ? "default" : "outline"} className={cn("w-full max-w-[180px] rounded-full", i === 1 ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border-primary/20 text-primary hover:bg-primary/10 hover:text-primary")}>
                        <Link to={pkg.link}>{pkg.cta}</Link>
                      </Button>
                    </div>
                  </TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparisonData.map((row, index) => <TableRow key={row.feature} data-index={index} className={cn("border-0 hover:bg-muted/20 transition-colors", index % 2 === 0 ? "bg-muted/10" : "bg-background")}>
                  <TableCell className="py-5 px-6 font-medium text-muted-foreground">
                    {row.feature}
                  </TableCell>
                  <TableCell className="py-5 px-6 text-center border-l border-border/50">
                    {renderCell(row.starter)}
                  </TableCell>
                  <TableCell className="py-5 px-6 text-center border-l border-border/50">
                    {renderCell(row.advanced)}
                  </TableCell>
                  <TableCell className="py-5 px-6 text-center border-l border-border/50">
                    {renderCell(row.premium)}
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </div>

        <div className="lg:hidden">
          <Tabs defaultValue="starter" className="w-full">
            <TabsList className="w-full grid grid-cols-3 bg-muted p-1 rounded-xl mb-8">
              {packages.map((pkg, i) => <TabsTrigger key={pkg.id} value={pkg.id} data-index={i} className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg text-xs sm:text-sm py-2.5">
                  {pkg.name}
                </TabsTrigger>)}
            </TabsList>

            {packages.map((pkg, i) => <TabsContent key={pkg.id} value={pkg.id} data-index={i}>
                <Card className="bg-card border-border shadow-lg overflow-hidden">
                  <CardHeader className="bg-muted/20 border-b border-border pb-8 pt-8 text-center">
                    {i === 1 && <div className="bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full w-fit mx-auto mb-2">
                        Popular
                      </div>}
                    <CardTitle className="text-2xl font-bold text-primary mb-2">
                      {pkg.name}
                    </CardTitle>
                    <div className="flex items-baseline justify-center gap-1 mb-2">
                      <span className="text-3xl font-bold text-foreground">
                        {pkg.price}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {pkg.gst}
                      </span>
                    </div>
                    <CardDescription className="text-muted-foreground">
                      {pkg.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="flex flex-col">
                      {comparisonData.map((row, rowIndex) => {
                    const value = row[pkg.id as keyof typeof row] as string | boolean;
                    return <div key={rowIndex} data-index={rowIndex} className={cn("flex items-center justify-between py-4 px-6 border-b border-border last:border-0", rowIndex % 2 === 0 ? "bg-muted/10" : "bg-background")}>
                            <span className="text-sm font-medium text-muted-foreground">
                              {row.feature}
                            </span>
                            <div className="text-sm">
                              {renderCell(value)}
                            </div>
                          </div>;
                  })}
                    </div>
                  </CardContent>
                  <CardFooter className="bg-muted/20 p-6 border-t border-border">
                    <Button asChild className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base">
                      <Link to={pkg.link}>{pkg.cta}</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}